import { useState, useCallback } from 'react';

interface FormErrors {
  [key: string]: string;
}

interface FormData {
  [key: string]: any;
}

interface UseFormDataReturn {
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSelectChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  handleCheckbox: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleErrors: (error: FormErrors) => void;
  resetField: (fieldName: string) => void;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  setFormErrors: React.Dispatch<React.SetStateAction<FormErrors>>;
  formData: FormData;
  formErrors: FormErrors;
  removeFormError: (fieldName: string) => void;
  updatingForm: boolean;
  setUpdatingForm: React.Dispatch<React.SetStateAction<boolean>>;
}

const useFormData = (initialFormData: FormData = {}): UseFormDataReturn => {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [updatingForm, setUpdatingForm] = useState<boolean>(false);

  const handleCheckbox = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.persist();
      setFormData((prevState) => ({
        ...prevState,
        [e.target.name]: e.target.checked,
      }));
    },
    [setFormData],
  );

  const handleErrors = useCallback(
    (error: FormErrors) => {
      setFormErrors((prevState) => ({
        ...prevState,
        ...error,
      }));
    },
    [setFormErrors],
  );

  const removeFormError = useCallback(
    (fieldName: string) => {
      setFormErrors((prevState) => {
        const newState = { ...prevState };
        delete newState[fieldName];
        return newState;
      });
    },
    [setFormErrors],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value, maxLength, step } = e.target;
      e.persist();
      removeFormError(name);
      setFormData((prevState) => ({
        ...prevState,
        [name]: value,
      }));

      // Display error if value is greater than character limit
      if (maxLength && value.length === maxLength && name !== 'sku') {
        handleErrors({
          [name]: `You have reached the ${maxLength
            .toString()
            .replace(/\B(?=(\d{3})+(?!\d))/g, ',')} character limit.`,
        });
      }
      // Price errors
      if (step === '.01') {
        if (parseFloat(value) < 0) {
          handleErrors({
            [name]: `Price cannot be less than 0.`,
          });
        }
        const valid = /^\d+(\.\d{1,2})?$/.test(value);
        if (!valid && value !== '') {
          handleErrors({
            [name]: `Price can only have two decimal points.`,
          });
        }
      }
    },
    [removeFormError, setFormData, handleErrors],
  );

  const handleSelectChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const { name, value } = e.target;
      e.persist();
      removeFormError(name);
      setFormData((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    },
    [removeFormError, setFormData],
  );

  const resetField = useCallback(
    (fieldName: string) => {
      setFormData((prevState) => ({
        ...prevState,
        [fieldName]: '',
      }));
    },
    [setFormData],
  );

  return {
    handleInputChange,
    handleSelectChange,
    handleCheckbox,
    handleErrors,
    resetField,
    setFormData,
    setFormErrors,
    formData,
    formErrors,
    removeFormError,
    updatingForm,
    setUpdatingForm,
  };
};

export default useFormData;
